import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { useAskAgain } from "../../api/refresh";
import { organizationSecurityQueryOptions, updateOrganizationSecurity } from "../../api/settings";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { Card, CardHeader } from "../../components/ui/card";
import { showToast } from "../../lib/toast";

export const Route = createFileRoute("/settings/security")({
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: ({ context }) => context.queryClient.query({ ...organizationSecurityQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Security · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Security"] },
	component: SecurityPage,
});

// Copied rather than imported from settings/index.tsx, which keeps its own small helper for the same reason.
function plural(count: number, singular: string, pluralForm: string): string {
	return `${count} ${count === 1 ? singular : pluralForm}`;
}

// Grid, not a flex row with a wrapped text column: the radio, the title and the description are all direct children
// of the label, which is what keeps the description's text within the accessibility linter's nesting depth.
const optionLabelClassName =
	"grid cursor-pointer grid-cols-[auto_1fr] items-start gap-x-2.5 gap-y-0.5 rounded-field border border-line bg-bg px-3 py-2.5";

function SecurityPage() {
	const { data: status } = useSuspenseQuery(organizationSecurityQueryOptions());
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	// The radio choice, starting at what is saved; Save stays disabled until it differs.
	const [requireMfa, setRequireMfa] = useState(status.requireMfa);

	const mutation = useMutation({
		mutationFn: updateOrganizationSecurity,
		// The sidebar and Team & Access read the same setting; the audit entry arrives through a queue a moment
		// after this answer, so it is asked again as it catches up.
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings"] }),
				askAgain(["settings", "audit-log"]),
			]),
	});

	const save = async () => {
		try {
			await mutation.mutateAsync({ requireMfa });
		} catch {
			// The alert below shows mutation.error.
			return;
		}
		showToast("Security settings saved.");
	};

	const notEveryoneReady = status.membersWithMfa < status.memberCount;
	const changed = requireMfa !== status.requireMfa;

	return (
		<>
			<PageHeader title="Security" description="How everyone in the organization signs in." />
			<form
				noValidate
				className="flex flex-col gap-3.5"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void save();
				}}
			>
				<Card>
					<CardHeader title="Two-step verification" />
					<div className="flex flex-col gap-3 p-4">
						<fieldset className="flex flex-col gap-2 border-0 p-0">
							<legend className="sr-only">Two-step verification</legend>
							<label className={optionLabelClassName}>
								<input
									type="radio"
									name="requireMfa"
									className="row-span-2 mt-1"
									checked={!requireMfa}
									onChange={() => {
										setRequireMfa(false);
									}}
								/>
								<span className="text-body font-medium text-ink">Optional</span>
								<span className="text-caption text-ink-2">
									Members may set up an authenticator app if they want to.
								</span>
							</label>
							<label className={optionLabelClassName}>
								<input
									type="radio"
									name="requireMfa"
									className="row-span-2 mt-1"
									checked={requireMfa}
									onChange={() => {
										setRequireMfa(true);
									}}
								/>
								<span className="text-body font-medium text-ink">Required for everyone</span>
								<span className="text-caption text-ink-2">
									Anyone without an authenticator app is asked to set one up the next time they sign
									in.
								</span>
							</label>
						</fieldset>
						<p className="text-caption text-ink-2">
							{status.membersWithMfa} of {plural(status.memberCount, "member has", "members have")} set
							one up.
						</p>
						{requireMfa && notEveryoneReady ? (
							<p className="text-caption text-warn">
								Members without an authenticator app will be asked to set one up before they can sign in
								again. An owner or admin can reset one under Team & Access if someone loses their phone.
							</p>
						) : null}
					</div>
				</Card>
				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<div>
					<Button type="submit" variant="primary" disabled={!changed || mutation.isPending}>
						{mutation.isPending ? "Saving…" : "Save security settings"}
					</Button>
				</div>
			</form>
		</>
	);
}
